import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/api/types/ai-monitoring";

export default class APIAIMonitoring extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  /**
   * Get AI accuracy statistics
   */
  getStats = async (params?: {
    time_granularity?: "5min" | "1h" | "4h" | "1d";
    start_date?: string;
    end_date?: string;
  }): Promise<types.AIAccuracyStats> => {
    const searchParams = new URLSearchParams();
    if (params?.time_granularity)
      searchParams.append("time_granularity", params.time_granularity);
    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);

    const queryString = searchParams.toString();
    const url = `/ai-monitoring/stats${queryString ? `?${queryString}` : ""}`;

    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get accuracy trends over time
   */
  getTrends = async (params?: {
    start_date?: string;
    end_date?: string;
    granularity?: "5min" | "1h" | "4h" | "1d";
  }): Promise<types.AIAccuracyTrendPoint[]> => {
    const searchParams = new URLSearchParams();
    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    if (params?.granularity)
      searchParams.append("granularity", params.granularity);

    const queryString = searchParams.toString();
    const url = `/ai-monitoring/trends${queryString ? `?${queryString}` : ""}`;

    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get paginated list of corrections
   */
  getCorrections = async (params?: {
    start_date?: string;
    end_date?: string;
    field_name?: string;
    limit?: number;
    offset?: number;
  }): Promise<types.CorrectionsPaginatedResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    if (params?.field_name)
      searchParams.append("field_name", params.field_name);
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.offset) searchParams.append("offset", params.offset.toString());

    const queryString = searchParams.toString();
    const url = `/ai-monitoring/corrections${queryString ? `?${queryString}` : ""}`;

    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get extraction details for an agent execution
   */
  getExtractionDetails = async (
    agentExecutionId: string,
  ): Promise<types.ExtractionDetails> => {
    const res = await this._fetch({
      url: `/ai-monitoring/extraction/${agentExecutionId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Export corrections as CSV (returns blob URL)
   */
  exportCorrectionsCSV = async (params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);

    const queryString = searchParams.toString();
    const url = `/ai-monitoring/corrections/export${queryString ? `?${queryString}` : ""}`;

    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  // ============================================
  // Change Audit Endpoints (user/AI change tracking)
  // ============================================

  /**
   * Get paginated list of change audit records
   */
  getChangeAudits = async (
    params?: types.ChangeAuditFilters,
  ): Promise<types.ChangeAuditListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    if (params?.actor_type)
      searchParams.append("actor_type", params.actor_type);
    if (params?.entity_type)
      searchParams.append("entity_type", params.entity_type);
    if (params?.field_name)
      searchParams.append("field_name", params.field_name);
    if (params?.pre_order_id)
      searchParams.append("pre_order_id", params.pre_order_id);
    if (params?.user_id) searchParams.append("user_id", params.user_id);
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.offset) searchParams.append("offset", params.offset.toString());

    const queryString = searchParams.toString();
    const url = `/ai-monitoring/changes${queryString ? `?${queryString}` : ""}`;

    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get change audit statistics
   */
  getChangeAuditStatistics = async (params?: {
    days?: number;
  }): Promise<types.ChangeAuditStatistics> => {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append("days", params.days.toString());

    const queryString = searchParams.toString();
    const url = `/ai-monitoring/change-statistics${queryString ? `?${queryString}` : ""}`;

    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get changes for a specific entity
   */
  getChangesByEntity = async (
    entityType: string,
    entityId: string,
  ): Promise<types.ChangeAuditItem[]> => {
    const searchParams = new URLSearchParams();
    searchParams.append("entity_type", entityType);
    searchParams.append("entity_id", entityId);

    const url = `/ai-monitoring/changes/by-entity?${searchParams.toString()}`;

    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };
}
